// ============================================================
// useGridGeometry — グリッド座標計算ユーティリティ
//
// 既存の calendarGeometry.js をラップ/再実装。
// Y軸: 日付+時刻 (下方向)
// X軸: 車両列 (右方向)
// ============================================================

import { GRID_CONFIG } from './types';

// TIME_SLOTS の定義（calendarGeometry.js の TIME_SLOTS と同等）
export const TIME_SLOTS = [
  { type: 'bucket' as const, id: 'morning', label: '早朝', hours: [0, 1, 2, 3, 4, 5, 6, 7, 8] },
  ...Array.from({ length: 13 }, (_, i) => ({
    type: 'hour' as const,
    hour: 9 + i,
    label: `${String(9 + i).padStart(2, '0')}:00`,
  })),
  { type: 'bucket' as const, id: 'night', label: '深夜', hours: [22, 23] },
];

export const SLOTS_PER_DAY = TIME_SLOTS.length; // 15
export const DAY_HEIGHT_PX = GRID_CONFIG.slotHeight * SLOTS_PER_DAY; // 36 × 15 = 540

/** 日付を "YYYY-MM-DD" の日付部分のみに正規化 */
export const toStartOfDay = (input: Date | string): Date => {
  const d = input instanceof Date ? input : new Date(input);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** rangeStart から targetDate までの日数差 */
export const diffInDays = (base: Date, target: Date): number => {
  const a = toStartOfDay(base).getTime();
  const b = toStartOfDay(target).getTime();
  return Math.floor((b - a) / 86400000);
};

/** hour に対応する TIME_SLOTS のインデックスを返す */
export const findSlotIndex = (hour: number): number => {
  let idx = TIME_SLOTS.findIndex(
    (s) => s.type === 'hour' && s.hour === hour,
  );
  if (idx !== -1) return idx;

  idx = TIME_SLOTS.findIndex(
    (s) => s.type === 'bucket' && s.hours.includes(hour),
  );
  if (idx !== -1) return idx;

  // hour が 9 未満なら morning bucket (0)
  // hour が 22 以上なら night bucket (最後)
  if (hour < 9) return 0;
  return TIME_SLOTS.length - 1;
};

/** スロットの分数と開始時刻を返す */
const resolveSlotMeta = (slot: typeof TIME_SLOTS[number]) => {
  if (slot.type === 'hour') {
    return { minutes: 60, startHour: slot.hour };
  }
  const startHour = slot.hours.length > 0 ? Math.min(...slot.hours) : 0;
  return { minutes: slot.hours.length * 60, startHour };
};

/**
 * 日時 → キャンバス上の絶対 Y 座標を計算（スナップなし）
 * @param rangeStart グリッドの表示開始日
 * @param date       対象の日時
 */
export const dateToAbsoluteY = (rangeStart: Date, date: Date): number => {
  const dayIndex = diffInDays(rangeStart, date);
  const slotIndex = findSlotIndex(date.getHours());
  const slot = TIME_SLOTS[slotIndex];
  const { minutes: slotMinutes, startHour } = resolveSlotMeta(slot);
  const minutesInSlot =
    (date.getHours() - startHour) * 60 +
    date.getMinutes() +
    date.getSeconds() / 60;
  const clamped = Math.max(0, Math.min(slotMinutes, minutesInSlot));
  const fraction = slotMinutes > 0 ? clamped / slotMinutes : 0;
  const yWithinDay = slotIndex * GRID_CONFIG.slotHeight + fraction * GRID_CONFIG.slotHeight;
  return dayIndex * DAY_HEIGHT_PX + yWithinDay;
};

/**
 * 車両インデックス → キャンバス上の X 座標（左端）
 */
export const vehicleIndexToX = (index: number): number =>
  index * GRID_CONFIG.columnWidth;

/**
 * 全体キャンバスサイズ（左ガターを除く本体部分）
 */
export const getBodyCanvasSize = (vehicleCount: number, dayCount: number) => ({
  width: vehicleCount * GRID_CONFIG.columnWidth,
  height: dayCount * DAY_HEIGHT_PX,
});
